import { Baby } from '../models';
import { VariationCatalogOption } from './variationCatalog';

const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})/;
const MONTHS_PER_YEAR = 12;
const MAX_MONTHS_SHOWN_AS_MONTHS = 11;

export interface CivilDate {
  year: number;
  month: number;
  day: number;
}

export function parseDateOnly(value: string | null | undefined): CivilDate | null {
  const match = value ? DATE_ONLY_REGEX.exec(value) : null;
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

export function todayCivilDate(): CivilDate {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

export function ageInMonths(birthdate: string | null | undefined, today: CivilDate = todayCivilDate()): number | null {
  const birth = parseDateOnly(birthdate);
  if (!birth) return null;
  let months = (today.year - birth.year) * MONTHS_PER_YEAR + (today.month - birth.month);
  if (today.day < birth.day) months -= 1;
  return months >= 0 ? months : null;
}

export function formatAgeMonths(months: number): string {
  if (months <= MAX_MONTHS_SHOWN_AS_MONTHS) {
    return `${months} ${months === 1 ? 'mês' : 'meses'}`;
  }
  const years = Math.floor(months / MONTHS_PER_YEAR);
  return `${years} ${years === 1 ? 'ano' : 'anos'}`;
}

export function optionsForAge(ageMonths: number, options: VariationCatalogOption[]): VariationCatalogOption[] {
  return options.filter(
    (option) =>
      option.minAgeMonths !== null &&
      option.maxAgeMonths !== null &&
      ageMonths >= option.minAgeMonths &&
      ageMonths <= option.maxAgeMonths
  );
}

export interface BabySizeMatch {
  baby: Baby;
  ageMonths: number;
  options: VariationCatalogOption[];
}

export function matchBabiesToOptions(babies: Baby[], options: VariationCatalogOption[]): BabySizeMatch[] {
  const matches: BabySizeMatch[] = [];
  for (const baby of babies) {
    const ageMonths = ageInMonths(baby.birthdate);
    if (ageMonths === null) continue;
    const matched = optionsForAge(ageMonths, options);
    if (matched.length > 0) matches.push({ baby, ageMonths, options: matched });
  }
  return matches;
}

const GENDER_MALE = 1;
const GENDER_FEMALE = 2;
const GENDER_UNISEX = 4;

export function childGenderFilter(baby: Baby): number | undefined {
  return baby.gender === GENDER_MALE || baby.gender === GENDER_FEMALE ? baby.gender : undefined;
}

export function productFitsChild(productGender: number, baby: Baby): boolean {
  const childGender = childGenderFilter(baby);
  return childGender === undefined || productGender === childGender || productGender === GENDER_UNISEX;
}

const FALLBACK_BABY_NAME = 'sua criança';

export function babyFirstName(baby: Baby): string {
  return baby.name.trim().split(/\s+/)[0] || FALLBACK_BABY_NAME;
}

export function sizeBadgeLabel(babies: Baby[]): string {
  const names = babies.map(babyFirstName);
  if (names.length === 1) {
    const article = babies[0].gender === GENDER_MALE ? 'do' : babies[0].gender === GENDER_FEMALE ? 'da' : 'de';
    return `Tamanho ${article} ${names[0]}`;
  }
  const last = names[names.length - 1];
  return `Tamanho de ${names.slice(0, -1).join(', ')} e ${last}`;
}
